import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db, storage } from '../firebase';
import { 
  collection, addDoc, updateDoc, deleteDoc, doc, setDoc,
  onSnapshot, query, orderBy, writeBatch, increment, 
  serverTimestamp, getDocs, where 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { user } = useAuth();
  
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [projectMessages, setProjectMessages] = useState([]);
  
  const [activeTimer, setActiveTimer] = useState(null);
  const [loading, setLoading] = useState(true);

  // Default Settings
  const defaultSettings = {
    smtp: {
      host: '',
      port: '',
      username: '',
      password: '',
      fromName: 'CRM System',
      fromEmail: '',
      encryption: 'tls',
      enabled: false,
      notifications: {
        taskAssigned: true,
        taskCompleted: true,
        deadlineWarning: true
      }
    },
    branding: {
      appName: 'CRM Pro',
      logoUrl: '',
      primaryColor: '#2563eb',
      companyName: 'My Company',
      footerText: '© 2024 All rights reserved.'
    }
  };

  const [settings, setSettings] = useState(defaultSettings);

  // Subscribe to Firestore Collections
  useEffect(() => {
    if (!user) {
      setTasks([]);
      setProjects([]);
      setClients([]);
      setTeamMembers([]);
      setLeaves([]);
      setProjectMessages([]);
      return;
    }

    setLoading(true);

    // Tasks Subscription
    const qTasks = query(collection(db, 'tasks'), orderBy('createdAt', 'desc'));
    const unsubTasks = onSnapshot(qTasks, (snapshot) => {
      setTasks(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Projects Subscription
    const qProjects = query(collection(db, 'projects'), orderBy('createdAt', 'desc'));
    const unsubProjects = onSnapshot(qProjects, (snapshot) => {
      setProjects(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Clients Subscription
    const qClients = query(collection(db, 'clients'), orderBy('createdAt', 'desc'));
    const unsubClients = onSnapshot(qClients, (snapshot) => {
      setClients(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Team Members (Users) Subscription
    const qUsers = query(collection(db, 'users'));
    const unsubUsers = onSnapshot(qUsers, (snapshot) => {
      setTeamMembers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Leaves Subscription
    const qLeaves = query(collection(db, 'leaves'), orderBy('createdAt', 'desc'));
    const unsubLeaves = onSnapshot(qLeaves, (snapshot) => {
      setLeaves(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Messages Subscription
    const qMessages = query(collection(db, 'messages'), orderBy('createdAt', 'asc'));
    const unsubMessages = onSnapshot(qMessages, (snapshot) => {
      setProjectMessages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Settings Subscription (Global Configuration)
    const settingsRef = doc(db, 'config', 'appSettings');
    const unsubSettings = onSnapshot(settingsRef, (docSnap) => {
      if (docSnap.exists()) {
        setSettings(docSnap.data());
      } else {
        // Initialize default settings if not exists
        setDoc(settingsRef, defaultSettings).catch(err => console.error("Error init settings:", err));
      }
    });

    setLoading(false);

    return () => {
      unsubTasks();
      unsubProjects();
      unsubClients();
      unsubUsers();
      unsubLeaves();
      unsubMessages();
      unsubSettings();
    };
  }, [user]);

  // --- CRUD Operations ---

  const createTask = async (taskData) => {
    await addDoc(collection(db, 'tasks'), {
      ...taskData,
      createdAt: new Date().toISOString(),
      comments: [],
      timeLog: []
    });
  };

  const updateTask = async (taskId, updates) => {
    const taskRef = doc(db, 'tasks', taskId);
    await updateDoc(taskRef, updates);
  };

  const addComment = async (taskId, comment) => {
    const taskRef = doc(db, 'tasks', taskId);
    const task = tasks.find(t => t.id === taskId);
    const newComment = {
      id: Date.now().toString(),
      userId: user.id,
      text: comment,
      createdAt: new Date().toISOString()
    };
    await updateDoc(taskRef, {
      comments: [...(task.comments || []), newComment]
    });
  };

  const addTimeLog = async (taskId, timeEntry) => {
    const taskRef = doc(db, 'tasks', taskId);
    const task = tasks.find(t => t.id === taskId);
    const newLog = {
      id: Date.now().toString(),
      userId: user.id,
      ...timeEntry,
      date: new Date().toISOString()
    };
    await updateDoc(taskRef, {
      timeLog: [...(task.timeLog || []), newLog]
    });
  };

  // Project Management
  const createProject = async (projectData) => {
    await addDoc(collection(db, 'projects'), {
      ...projectData,
      createdAt: new Date().toISOString(),
      progress: 0,
      milestones: [],
      risks: [],
      files: [],
      budget: projectData.budget || { total: 0, spent: 0, currency: 'USD' }
    });
  };

  const updateProject = async (projectId, updates) => {
    const projectRef = doc(db, 'projects', projectId);
    await updateDoc(projectRef, updates);
  };

  const addProjectMessage = async (projectId, text) => {
    await addDoc(collection(db, 'messages'), {
      projectId,
      userId: user.id,
      text,
      createdAt: new Date().toISOString()
    });
  };

  const addProjectFile = async (projectId, file) => {
    try {
      // Create a reference to the file in Firebase Storage
      const fileRef = ref(storage, `projects/${projectId}/${Date.now()}_${file.name}`);
      
      // Upload the file
      const snapshot = await uploadBytes(fileRef, file);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Update Firestore with file metadata and URL
      const projectRef = doc(db, 'projects', projectId);
      const project = projects.find(p => p.id === projectId);
      
      const newFile = {
        id: Date.now().toString(),
        name: file.name,
        size: (file.size / 1024).toFixed(2) + ' KB',
        type: file.name.split('.').pop(),
        uploadDate: new Date().toISOString(),
        url: downloadURL
      };
      
      await updateDoc(projectRef, {
        files: [newFile, ...(project.files || [])]
      });
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file. Please check your connection.");
    }
  };

  const createClient = async (clientData) => {
    await addDoc(collection(db, 'clients'), {
      ...clientData,
      createdAt: new Date().toISOString(),
      lastContact: new Date().toISOString()
    });
  };

  const updateClient = async (clientId, updates) => {
    const clientRef = doc(db, 'clients', clientId);
    await updateDoc(clientRef, updates);
  };

  // Team Management
  const addTeamMember = async (memberData) => {
    // Adds a user profile.
    await addDoc(collection(db, 'users'), {
      ...memberData,
      joinDate: new Date().toISOString(),
      status: 'active',
      performance: { score: 0, tasksCompleted: 0, avgRating: 0 },
      leaveBalance: memberData.leaveBalance || { casual: 12, paid: 18 }
    });
  };

  const updateTeamMember = async (id, updates) => {
    const memberRef = doc(db, 'users', id);
    await updateDoc(memberRef, updates);
  };

  const deleteTeamMember = async (id) => {
    const memberRef = doc(db, 'users', id);
    await deleteDoc(memberRef);
  };

  // Leave Management
  const requestLeave = async (leaveData) => {
    await addDoc(collection(db, 'leaves'), {
      userId: user.id,
      ...leaveData,
      status: 'pending',
      createdAt: new Date().toISOString()
    });
  };

  const updateLeaveStatus = async (leaveId, status) => {
    const leaveRef = doc(db, 'leaves', leaveId);
    await updateDoc(leaveRef, { status });
  };

  const updateLeave = async (leaveId, updates) => {
    const leaveRef = doc(db, 'leaves', leaveId);
    await updateDoc(leaveRef, updates);
  };

  const deleteLeave = async (leaveId) => {
    const leaveRef = doc(db, 'leaves', leaveId);
    await deleteDoc(leaveRef);
  };

  // Bulk update leave balances
  const bulkUpdateLeaveBalances = async (memberIds, casualAdd, paidAdd) => {
    const batch = writeBatch(db);
    memberIds.forEach(id => {
      const userRef = doc(db, 'users', id);
      batch.update(userRef, {
        "leaveBalance.casual": increment(Number(casualAdd)),
        "leaveBalance.paid": increment(Number(paidAdd))
      });
    });
    await batch.commit();
  };

  // Bulk expire/reset leaves
  const bulkExpireLeaves = async (type, maxBalance) => {
    const batch = writeBatch(db);
    teamMembers.forEach(member => {
      const currentBalance = member.leaveBalance || { casual: 0, paid: 0 };
      const currentVal = Number(currentBalance[type]) || 0;
      if (currentVal > maxBalance) {
        const userRef = doc(db, 'users', member.id);
        batch.update(userRef, {
          [`leaveBalance.${type}`]: Number(maxBalance)
        });
      }
    });
    await batch.commit();
  };

  // Timer Management
  const startTimer = (taskId, description) => {
    setActiveTimer({
      taskId,
      startTime: Date.now(),
      description
    });
  };

  const stopTimer = () => {
    if (activeTimer) {
      const duration = (Date.now() - activeTimer.startTime) / (1000 * 60 * 60); // hours
      const roundedDuration = Math.max(0.01, Math.round(duration * 100) / 100);
      addTimeLog(activeTimer.taskId, {
        hours: roundedDuration,
        description: activeTimer.description || 'Timer session'
      });
      setActiveTimer(null);
    }
  };

  const updateSettings = async (section, newSettings) => {
    const settingsRef = doc(db, 'config', 'appSettings');
    const updatedSection = { ...settings[section], ...newSettings };
    const updatedSettings = { ...settings, [section]: updatedSection };
    
    // Optimistic update
    setSettings(updatedSettings);
    
    await setDoc(settingsRef, updatedSettings, { merge: true });
  };

  const value = {
    tasks,
    projects,
    clients,
    teamMembers,
    leaves,
    projectMessages,
    activeTimer,
    loading,
    settings,
    createTask,
    updateTask,
    addComment,
    addTimeLog,
    createProject,
    updateProject,
    addProjectMessage,
    addProjectFile,
    createClient,
    updateClient,
    addTeamMember,
    updateTeamMember,
    deleteTeamMember,
    requestLeave,
    updateLeaveStatus,
    updateLeave,
    deleteLeave,
    bulkUpdateLeaveBalances,
    bulkExpireLeaves,
    startTimer,
    stopTimer,
    updateSettings
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};